import type { AuthTokenPayload } from "./types";
import { hasPermission } from "./matching";
import { isPlatformAdmin } from "./jwt";
import {
  type CanonicalRole,
  type RoleOrgType,
  CANONICAL_ROLE_LABELS,
  expandRoleForSites,
  isRoleOrgType,
  legacyRoleToCanonical,
} from "./role-templates";

export type RoleDisplayLabel =
  | (typeof CANONICAL_ROLE_LABELS)[CanonicalRole]
  | "Custom"
  | "Unknown";

export interface UserPermissionsSource {
  data?: {
    permissions?: string[] | Record<string, string[]>;
    sites?: Record<string, string[]> | null;
  } | null;
}

export function permissionsForOrg(all: readonly string[], orgId: string): string[] {
  const prefix = `recyclee.${orgId}.`;
  return all.filter((p) => p.startsWith(prefix) || p === `recyclee.${orgId}.*`);
}

export function filterGrantablePermissions(
  requesterPerms: readonly string[],
  candidates: readonly string[],
  payload: AuthTokenPayload | null | undefined,
): string[] {
  if (payload && isPlatformAdmin(payload)) {
    return [...candidates];
  }
  return candidates.filter((p) => hasPermission(requesterPerms, p));
}

export function assertCanGrantAll(
  requesterPerms: readonly string[],
  toGrant: readonly string[],
  payload: AuthTokenPayload | null | undefined,
): void {
  if (payload && isPlatformAdmin(payload)) {
    return;
  }
  for (const p of toGrant) {
    if (!hasPermission(requesterPerms, p)) {
      throw new Error(`Cannot grant ${p}`);
    }
  }
}

export function canGrantRolePreset(
  requesterPerms: readonly string[],
  payload: AuthTokenPayload | null | undefined,
  orgType: RoleOrgType,
  orgId: string,
  role: CanonicalRole,
  siteIds: string[] | "*",
): boolean {
  if (role === "admin") {
    const isOrgAdmin =
      isPlatformAdmin(payload) || hasPermission(requesterPerms, `recyclee.${orgId}.*`);
    if (!isOrgAdmin) {
      return false;
    }
  }
  try {
    const expanded = expandRoleForSites(orgType, role, orgId, siteIds);
    assertCanGrantAll(requesterPerms, expanded, payload);
    return true;
  } catch {
    return false;
  }
}

export function inferCanonicalRole(
  user: UserPermissionsSource,
  orgId: string,
  orgType: RoleOrgType,
): CanonicalRole | "custom" | "unknown" {
  const perms = user.data?.permissions;
  if (!perms) {
    return "unknown";
  }

  if (typeof perms === "object" && !Array.isArray(perms)) {
    const roles = perms[orgId] ?? [];
    for (const r of roles) {
      const canonical = legacyRoleToCanonical(String(r));
      if (canonical) {
        return canonical;
      }
    }
    return "unknown";
  }

  if (Array.isArray(perms)) {
    const orgPerms = permissionsForOrg(perms, orgId);
    const siteEntry = user.data?.sites?.[orgId] ?? "*";
    for (const role of ["admin", "ops_manager", "staff", "admin_read_only"] as const) {
      const template = expandRoleForSites(orgType, role, orgId, siteEntry);
      if (
        template.every((t) => hasPermission(orgPerms, t)) &&
        orgPerms.length <= template.length + 3
      ) {
        return role;
      }
    }
    return "custom";
  }

  return "unknown";
}

export function inferRoleLabel(
  user: UserPermissionsSource,
  orgId: string,
  orgType: RoleOrgType | string,
): RoleDisplayLabel {
  if (!isRoleOrgType(orgType as RoleOrgType)) {
    return "Unknown";
  }
  const roleOrgType = orgType as RoleOrgType;
  const canonical = inferCanonicalRole(user, orgId, roleOrgType);
  if (canonical === "custom") return "Custom";
  if (canonical === "unknown") return "Unknown";
  return CANONICAL_ROLE_LABELS[canonical];
}

export function mergeFlatPermissionsForOrgSave(
  existing: readonly string[],
  orgId: string,
  newOrgPerms: readonly string[],
): string[] {
  const prefix = `recyclee.${orgId}.`;
  const kept = existing.filter((p) => !p.startsWith(prefix) && p !== `recyclee.${orgId}.*`);
  return [...kept, ...newOrgPerms];
}

export function canUserDo(
  userPerms: readonly string[],
  orgId: string,
  entity: string,
  action: string,
  siteId = "*",
): boolean {
  const required = `recyclee.${orgId}.${siteId}.${entity}.${action}`;
  return hasPermission(userPerms, required);
}

export function permissionsChanged(
  before: string[] | Record<string, string[]> | undefined,
  after: string[] | Record<string, string[]> | undefined,
): boolean {
  return JSON.stringify(before ?? null) !== JSON.stringify(after ?? null);
}

export function flatPermissionsFromUser(user: UserPermissionsSource): string[] {
  const perms = user.data?.permissions;
  if (Array.isArray(perms)) {
    return perms.map(String);
  }
  return [];
}
